/**
 * Data models and interfaces for the Electricity Billing System
 * Based on the design document specifications
 */
/* eslint-disable max-classes-per-file */

// Cost Category Enum
export const CostCategory = {
  KWH_FIXED_CHARGE: 'KWH_FIXED_CHARGE',
  ON_PEAK_UNIT_CHARGE: 'ON_PEAK_UNIT_CHARGE',
  OFF_PEAK_UNIT_CHARGE: 'OFF_PEAK_UNIT_CHARGE',
  SUNDAY_UNIT_CHARGE: 'SUNDAY_UNIT_CHARGE',
  MISC_UNIT_CHARGE: 'MISC_UNIT_CHARGE',
  ON_PEAK_DEMAND_CHARGE: 'ON_PEAK_DEMAND_CHARGE',
  OFF_PEAK_DEMAND_CHARGE: 'OFF_PEAK_DEMAND_CHARGE',
  SUNDAY_DEMAND_CHARGE: 'SUNDAY_DEMAND_CHARGE',
  OIL_SURCHARGE: 'OIL_SURCHARGE',
  KVA_PER_UNIT_CHARGE: 'KVA_PER_UNIT_CHARGE',
  SERVICE_CHARGE: 'SERVICE_CHARGE',
  VAT: 'VAT',
  REA: 'REA',
  EWURA: 'EWURA',
};

// Rate Type Enum
export const RateType = {
  FIXED: 'FIXED',
  PERCENTAGE: 'PERCENTAGE',
};

/**
 * Rate Master Interface
 * Represents billing rate configurations with temporal validity
 */
export class RateMaster {
  constructor({
    id = null,
    _id = null,
    costCategory,
    rate,
    rateType = RateType.FIXED,
    effectiveFrom,
    effectiveTo,
    createdBy,
    createdAt = new Date(),
    isActive = true,
  }) {
    this.id = id;
    this._id = _id;
    this.costCategory = costCategory;
    this.rate = rate;
    this.rateType = rateType;
    this.effectiveFrom = new Date(effectiveFrom);
    this.effectiveTo = new Date(effectiveTo);
    this.createdBy = createdBy;
    this.createdAt = new Date(createdAt);
    this.isActive = isActive;
  }

  /**
   * Validates the rate master data
   * @returns {Object} validation result with isValid flag and errors array
   */
  validate() {
    const errors = [];

    if (!this.costCategory || !Object.values(CostCategory).includes(this.costCategory)) {
      errors.push('Cost category is required and must be valid');
    }

    if (typeof this.rate !== 'number' || this.rate < 0) {
      errors.push('Rate must be a non-negative number');
    }

    if (!Object.values(RateType).includes(this.rateType)) {
      errors.push('Rate type must be FIXED or PERCENTAGE');
    }

    if (!this.effectiveFrom || Number.isNaN(this.effectiveFrom.getTime())) {
      errors.push('Effective from date is required and must be valid');
    }

    if (!this.effectiveTo || Number.isNaN(this.effectiveTo.getTime())) {
      errors.push('Effective to date is required and must be valid');
    }

    if (this.effectiveFrom && this.effectiveTo && this.effectiveFrom >= this.effectiveTo) {
      errors.push('Effective from date must be before effective to date');
    }

    if (!this.createdBy || this.createdBy.trim() === '') {
      errors.push('Created by is required');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Checks if this rate is effective for a given date
   * @param {Date} date - The date to check
   * @returns {boolean} true if rate is effective for the date
   */
  isEffectiveForDate(date) {
    const checkDate = new Date(date);
    return checkDate >= this.effectiveFrom && checkDate <= this.effectiveTo && this.isActive;
  }
}

/**
 * Hourly Reading Interface
 * Represents individual hourly kWh readings from meters
 */
export class HourlyReading {
  constructor({
    timestamp,
    kwhValue,
    meterReading,
  }) {
    this.timestamp = new Date(timestamp);
    this.kwhValue = kwhValue;
    this.meterReading = meterReading;
  }

  validate() {
    const errors = [];

    if (!this.timestamp || Number.isNaN(this.timestamp.getTime())) {
      errors.push('Timestamp is required and must be valid');
    }

    if (typeof this.kwhValue !== 'number' || this.kwhValue < 0) {
      errors.push('kWh value must be a non-negative number');
    }

    if (typeof this.meterReading !== 'number' || this.meterReading < 0) {
      errors.push('Meter reading must be a non-negative number');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }
}

/**
 * Date Range Interface
 * Represents a date range for billing periods
 */
export class DateRange {
  constructor({ startDate, endDate }) {
    this.startDate = new Date(startDate);
    this.endDate = new Date(endDate);
  }

  validate() {
    const errors = [];

    if (!this.startDate || Number.isNaN(this.startDate.getTime())) {
      errors.push('Start date is required and must be valid');
    }

    if (!this.endDate || Number.isNaN(this.endDate.getTime())) {
      errors.push('End date is required and must be valid');
    }

    if (this.startDate && this.endDate && this.startDate >= this.endDate) {
      errors.push('Start date must be before end date');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }
}

/**
 * Consumption Data Interface
 * Represents electricity consumption data for a billing period
 * Supports both summary-based (flat-rate) and hourly-based (time-of-use) billing
 */
export class ConsumptionData {
  constructor({
    hourlyReadings = [],
    totalConsumption,
    initialReading,
    finalReading,
    billingPeriod,
    dataSource = 'manual',
    meterName = null,
    availableMeters = [],
  }) {
    // Only process hourly readings if they exist (for backward compatibility)
    this.hourlyReadings = Array.isArray(hourlyReadings)
      ? hourlyReadings.map((reading) => new HourlyReading(reading))
      : [];
    this.totalConsumption = totalConsumption;
    this.initialReading = initialReading;
    this.finalReading = finalReading;
    this.billingPeriod = new DateRange(billingPeriod);
    this.dataSource = dataSource; // 'automatic' or 'manual'
    this.meterName = meterName; // Name of the meter used (e.g., 'MAIN_HT_INCOMER')
    this.availableMeters = availableMeters || []; // List of available meters from API
  }

  validate() {
    const errors = [];

    if (typeof this.totalConsumption !== 'number' || this.totalConsumption < 0) {
      errors.push('Total consumption must be a non-negative number');
    }

    // In automatic mode, readings can be null
    if (this.dataSource === 'manual') {
      if (typeof this.initialReading !== 'number' || this.initialReading < 0) {
        errors.push('Initial reading must be a non-negative number');
      }

      if (typeof this.finalReading !== 'number' || this.finalReading < 0) {
        errors.push('Final reading must be a non-negative number');
      }

      if (this.finalReading < this.initialReading) {
        errors.push('Final reading must be greater than or equal to initial reading');
      }
    }

    // Validate billing period
    const periodValidation = this.billingPeriod.validate();
    if (!periodValidation.isValid) {
      errors.push(...periodValidation.errors);
    }

    // Validate hourly readings only if they exist (optional for flat-rate billing)
    if (this.hourlyReadings && this.hourlyReadings.length > 0) {
      this.hourlyReadings.forEach((reading, index) => {
        const readingValidation = reading.validate();
        if (!readingValidation.isValid) {
          errors.push(`Hourly reading ${index + 1}: ${readingValidation.errors.join(', ')}`);
        }
      });
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Calculates total consumption from hourly readings (if available)
   * @returns {number} sum of all hourly kWh values, or 0 if no hourly readings
   */
  calculateTotalFromHourly() {
    if (!this.hourlyReadings || this.hourlyReadings.length === 0) {
      return 0;
    }
    return this.hourlyReadings.reduce((total, reading) => total + reading.kwhValue, 0);
  }

  /**
   * Checks if this consumption data has hourly readings
   * @returns {boolean} true if hourly readings are available
   */
  hasHourlyReadings() {
    return this.hourlyReadings && this.hourlyReadings.length > 0;
  }

  /**
   * Gets the billing mode based on available data
   * @returns {string} 'summary' for flat-rate billing, 'hourly' for time-of-use billing
   */
  getBillingMode() {
    return this.hasHourlyReadings() ? 'hourly' : 'summary';
  }
}

/**
 * Calculated Charges Interface
 * Represents all calculated charge components for a bill
 */
export class CalculatedCharges {
  constructor({
    kwhCharges = 0,
    kvaCharges = 0,
    serviceCharges = 0,
    vatAmount = 0,
    reaAmount = 0,
    ewuraAmount = 0,
    totalCalculated = 0,
  }) {
    this.kwhCharges = kwhCharges;
    this.kvaCharges = kvaCharges;
    this.serviceCharges = serviceCharges;
    this.vatAmount = vatAmount;
    this.reaAmount = reaAmount;
    this.ewuraAmount = ewuraAmount;
    this.totalCalculated = totalCalculated;
  }

  validate() {
    const errors = [];
    const charges = [
      { name: 'kWh charges', value: this.kwhCharges },
      { name: 'kVA charges', value: this.kvaCharges },
      { name: 'Service charges', value: this.serviceCharges },
      { name: 'VAT amount', value: this.vatAmount },
      { name: 'REA amount', value: this.reaAmount },
      { name: 'EWURA amount', value: this.ewuraAmount },
      { name: 'Total calculated', value: this.totalCalculated },
    ];

    charges.forEach((charge) => {
      if (typeof charge.value !== 'number' || charge.value < 0) {
        errors.push(`${charge.name} must be a non-negative number`);
      }
    });

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Calculates the total from individual components
   * @returns {number} sum of all charge components
   */
  calculateTotal() {
    return this.kwhCharges + this.kvaCharges + this.serviceCharges
           + this.vatAmount + this.reaAmount + this.ewuraAmount;
  }
}

/**
 * Billing Record Interface
 * Represents a complete billing record with calculated and actual amounts
 */
export class BillingRecord {
  constructor({
    id = null,
    _id = null,
    billingPeriod,
    consumptionData,
    kvaDemand,
    calculatedCharges,
    actualBillAmount,
    actualKwhFromBill = 0,
    variance = 0,
    ratesUsed = [],
    createdBy,
    createdAt = new Date(),
  }) {
    this.id = id;
    this._id = _id;
    this.billingPeriod = new DateRange(billingPeriod);
    this.consumptionData = new ConsumptionData(consumptionData);
    this.kvaDemand = kvaDemand;
    // Handle both old format (CalculatedCharges object) and new format (dynamic object)
    if (calculatedCharges && typeof calculatedCharges === 'object') {
      // Always keep as raw object for dynamic charge breakdown
      // Supports both old format and dynamic charge keys like KWH_FIXED_CHARGE.
      this.calculatedCharges = calculatedCharges;
    } else {
      this.calculatedCharges = calculatedCharges || {};
    }
    this.actualBillAmount = actualBillAmount;
    this.actualKwhFromBill = actualKwhFromBill;
    this.variance = variance;
    this.ratesUsed = Array.isArray(ratesUsed)
      ? ratesUsed.map((rate) => new RateMaster(rate))
      : [];
    this.createdBy = createdBy;
    this.createdAt = new Date(createdAt);
  }

  validate() {
    const errors = [];

    // Validate billing period
    const periodValidation = this.billingPeriod.validate();
    if (!periodValidation.isValid) {
      errors.push(...periodValidation.errors.map((err) => `Billing period: ${err}`));
    }

    // Validate consumption data
    const consumptionValidation = this.consumptionData.validate();
    if (!consumptionValidation.isValid) {
      errors.push(...consumptionValidation.errors.map((err) => `Consumption data: ${err}`));
    }

    // Validate kVA demand
    if (typeof this.kvaDemand !== 'number' || this.kvaDemand < 0) {
      errors.push('kVA demand must be a non-negative number');
    }

    // Validate calculated charges
    if (this.calculatedCharges && typeof this.calculatedCharges === 'object') {
      // Validate that all charge values are numeric and non-negative
      Object.entries(this.calculatedCharges).forEach(([key, value]) => {
        if (typeof value !== 'number' || value < 0) {
          errors.push(`Calculated charges: ${key} must be a non-negative number`);
        }
      });

      // Ensure totalCalculated exists
      if (!('totalCalculated' in this.calculatedCharges)) {
        errors.push('Calculated charges: totalCalculated is required');
      }
    } else {
      errors.push('Calculated charges must be an object');
    }

    // Validate actual bill amount
    if (typeof this.actualBillAmount !== 'number' || this.actualBillAmount < 0) {
      errors.push('Actual bill amount must be a non-negative number');
    }

    // Validate variance
    if (typeof this.variance !== 'number') {
      errors.push('Variance must be a number');
    }

    // Validate created by
    if (!this.createdBy || this.createdBy.trim() === '') {
      errors.push('Created by is required');
    }

    // Validate rates used
    this.ratesUsed.forEach((rate, index) => {
      const rateValidation = rate.validate();
      if (!rateValidation.isValid) {
        errors.push(`Rate ${index + 1}: ${rateValidation.errors.join(', ')}`);
      }
    });

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Calculates variance between calculated and actual amounts
   * @returns {number} difference between actual and calculated amounts
   */
  calculateVariance() {
    const totalCalculated = this.calculatedCharges?.totalCalculated || 0;
    return this.actualBillAmount - totalCalculated;
  }

  /**
   * Updates the variance based on current amounts
   */
  updateVariance() {
    this.variance = this.calculateVariance();
  }
}

// Validation utility functions
export const ValidationUtils = {
  /**
   * Validates that a date falls within a given range
   * @param {Date} date - Date to validate
   * @param {Date} startDate - Range start date
   * @param {Date} endDate - Range end date
   * @returns {boolean} true if date is within range
   */
  isDateInRange(date, startDate, endDate) {
    const checkDate = new Date(date);
    const start = new Date(startDate);
    const end = new Date(endDate);
    return checkDate >= start && checkDate <= end;
  },

  /**
   * Checks if two date ranges overlap
   * @param {DateRange} range1 - First date range
   * @param {DateRange} range2 - Second date range
   * @returns {boolean} true if ranges overlap
   */
  doDateRangesOverlap(range1, range2) {
    return range1.startDate <= range2.endDate && range2.startDate <= range1.endDate;
  },

  /**
   * Validates that a number is within acceptable bounds
   * @param {number} value - Value to validate
   * @param {number} min - Minimum allowed value
   * @param {number} max - Maximum allowed value
   * @returns {boolean} true if value is within bounds
   */
  isNumberInRange(value, min = 0, max = Number.MAX_SAFE_INTEGER) {
    return typeof value === 'number' && value >= min && value <= max;
  },
};
/**
 * DG Consumption Data Interface
 * Represents diesel generator consumption data for a billing period
 */
export class DGConsumptionData {
  constructor({
    totalConsumption, // Total kWh consumed by DG
    initialReading, // Starting DG meter reading
    finalReading, // Ending DG meter reading
    billingPeriod, // Date range for the billing period
    meterReadings = [], // Array of individual DG readings (if available)
    dataSource = 'manual', // 'automatic' or 'manual'
    meterName = null, // Name of the DG meter (e.g., 'DG_1500KVA')
    availableMeters = [], // List of available meters from API
  }) {
    this.totalConsumption = totalConsumption;
    this.initialReading = initialReading;
    this.finalReading = finalReading;
    this.billingPeriod = new DateRange(billingPeriod);
    this.meterReadings = Array.isArray(meterReadings)
      ? meterReadings.map((reading) => new HourlyReading(reading))
      : [];
    this.dataSource = dataSource; // 'automatic' or 'manual'
    this.meterName = meterName; // Name of the DG meter used
    this.availableMeters = availableMeters || []; // List of available meters from API
  }

  validate() {
    const errors = [];

    if (typeof this.totalConsumption !== 'number' || this.totalConsumption < 0) {
      errors.push('Total DG consumption must be a non-negative number');
    }

    // In automatic mode, readings can be null
    if (this.dataSource === 'manual') {
      if (typeof this.initialReading !== 'number' || this.initialReading < 0) {
        errors.push('Initial DG reading must be a non-negative number');
      }

      if (typeof this.finalReading !== 'number' || this.finalReading < 0) {
        errors.push('Final DG reading must be a non-negative number');
      }

      if (this.finalReading < this.initialReading) {
        errors.push('Final DG reading must be greater than or equal to initial reading');
      }
    }

    // Validate billing period
    const periodValidation = this.billingPeriod.validate();
    if (!periodValidation.isValid) {
      errors.push(...periodValidation.errors);
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Calculates total consumption from meter readings
   * @returns {number} final reading minus initial reading
   */
  calculateTotalConsumption() {
    return this.finalReading - this.initialReading;
  }
}

/**
 * DG Efficiency Metrics Interface
 * Represents calculated efficiency metrics for diesel generator performance
 */
export class DGEfficiencyMetrics {
  constructor({
    kwhPerLitre = 0, // Power generation efficiency (kWh per litre of diesel)
    litresPerHour = 0, // Fuel consumption rate (litres per hour)
  }) {
    this.kwhPerLitre = kwhPerLitre;
    this.litresPerHour = litresPerHour;
  }

  validate() {
    const errors = [];

    if (typeof this.kwhPerLitre !== 'number' || this.kwhPerLitre < 0) {
      errors.push('kWh per litre must be a non-negative number');
    }

    if (typeof this.litresPerHour !== 'number' || this.litresPerHour < 0) {
      errors.push('Litres per hour must be a non-negative number');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Calculates kWh per litre efficiency
   * @param {number} totalConsumption - Total kWh consumed
   * @param {number} dieselConsumption - Total diesel consumed in litres
   * @returns {number} kWh per litre efficiency
   */
  static calculateKwhPerLitre(totalConsumption, dieselConsumption) {
    if (dieselConsumption === 0) {
      return 0; // Handle division by zero gracefully
    }
    return totalConsumption / dieselConsumption;
  }

  /**
   * Calculates litres per hour fuel consumption rate
   * @param {number} dieselConsumption - Total diesel consumed in litres
   * @param {number} runningHours - Total running hours
   * @returns {number} litres per hour fuel consumption rate
   */
  static calculateLitresPerHour(dieselConsumption, runningHours) {
    if (runningHours === 0) {
      return 0; // Handle division by zero gracefully
    }
    return dieselConsumption / runningHours;
  }
}

/**
 * DG Billing Record Interface
 * Represents a complete DG billing record with consumption, fuel usage, and efficiency metrics
 */
export class DGBillingRecord {
  constructor({
    id = null,
    _id = null,
    billingPeriod,
    dgConsumptionData,
    dieselConsumption, // Litres of diesel consumed (manual entry)
    runningHours, // Hours of DG operation (manual entry)
    efficiencyMetrics,
    createdBy,
    createdAt = new Date(),
    updatedAt = null,
  }) {
    this.id = id;
    this._id = _id;
    this.billingPeriod = new DateRange(billingPeriod);
    this.dgConsumptionData = new DGConsumptionData(dgConsumptionData);
    this.dieselConsumption = dieselConsumption;
    this.runningHours = runningHours;
    this.efficiencyMetrics = efficiencyMetrics
      ? new DGEfficiencyMetrics(efficiencyMetrics)
      : new DGEfficiencyMetrics({});
    this.createdBy = createdBy;
    this.createdAt = new Date(createdAt);
    this.updatedAt = updatedAt ? new Date(updatedAt) : null;
  }

  validate() {
    const errors = [];

    // Validate billing period
    const periodValidation = this.billingPeriod.validate();
    if (!periodValidation.isValid) {
      errors.push(...periodValidation.errors.map((err) => `Billing period: ${err}`));
    }

    // Validate DG consumption data
    const consumptionValidation = this.dgConsumptionData.validate();
    if (!consumptionValidation.isValid) {
      errors.push(...consumptionValidation.errors.map((err) => `DG consumption data: ${err}`));
    }

    // Validate diesel consumption
    if (typeof this.dieselConsumption !== 'number' || this.dieselConsumption <= 0) {
      errors.push('Diesel consumption must be a positive number');
    }

    // Validate running hours
    if (typeof this.runningHours !== 'number' || this.runningHours <= 0) {
      errors.push('Running hours must be a positive number');
    }

    // Validate efficiency metrics
    const metricsValidation = this.efficiencyMetrics.validate();
    if (!metricsValidation.isValid) {
      errors.push(...metricsValidation.errors.map((err) => `Efficiency metrics: ${err}`));
    }

    // Validate created by
    if (!this.createdBy || this.createdBy.trim() === '') {
      errors.push('Created by is required');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Calculates and updates efficiency metrics based on current data
   */
  calculateEfficiencyMetrics() {
    const kwhPerLitre = DGEfficiencyMetrics.calculateKwhPerLitre(
      this.dgConsumptionData.totalConsumption,
      this.dieselConsumption,
    );

    const litresPerHour = DGEfficiencyMetrics.calculateLitresPerHour(
      this.dieselConsumption,
      this.runningHours,
    );

    this.efficiencyMetrics = new DGEfficiencyMetrics({
      kwhPerLitre,
      litresPerHour,
    });
  }

  /**
   * Gets formatted efficiency metrics for display
   * @param {number} decimalPlaces - Number of decimal places for formatting
   * @returns {Object} formatted efficiency metrics
   */
  getFormattedEfficiencyMetrics(decimalPlaces = 2) {
    return {
      kwhPerLitre: this.efficiencyMetrics.kwhPerLitre.toFixed(decimalPlaces),
      litresPerHour: this.efficiencyMetrics.litresPerHour.toFixed(decimalPlaces),
    };
  }
}

// DG Billing utility functions
export const DGBillingUtils = {
  /**
   * Validates diesel consumption input
   * @param {number} value - Diesel consumption value
   * @returns {Object} validation result
   */
  validateDieselConsumption(value) {
    const errors = [];

    if (typeof value !== 'number') {
      errors.push('Diesel consumption must be a number');
    } else if (value <= 0) {
      errors.push('Diesel consumption must be greater than zero');
    } else if (!Number.isFinite(value)) {
      errors.push('Diesel consumption must be a finite number');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  },

  /**
   * Validates running hours input
   * @param {number} value - Running hours value
   * @returns {Object} validation result
   */
  validateRunningHours(value) {
    const errors = [];

    if (typeof value !== 'number') {
      errors.push('Running hours must be a number');
    } else if (value <= 0) {
      errors.push('Running hours must be greater than zero');
    } else if (!Number.isFinite(value)) {
      errors.push('Running hours must be a finite number');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  },

  /**
   * Formats efficiency metrics for display
   * @param {number} value - Efficiency value
   * @param {number} decimalPlaces - Number of decimal places
   * @returns {string} formatted value
   */
  formatEfficiencyMetric(value, decimalPlaces = 2) {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      return '0.00';
    }
    return value.toFixed(decimalPlaces);
  },
};
